'use client'

// The front door. First thing every visitor sees, so it does three jobs
// and no more: say his name like it means it, show that something is
// happening, and teach ONE thing you can do here per glance.
//
// The name is katakana at 900 weight, loaded as a real font rather than
// hoping the visitor's OS has a heavy Japanese face (most don't; the old
// veil rendered ピノ at whatever weight Hiragino felt like). The tips are
// hard-written in the bundle (lib/ui/loadingTips.js) so the first one is
// on screen the instant the veil is: no fetch, no failure mode, and a
// newcomer learns "you can leave him something and he remembers you"
// before the town has even arrived.
//
// The exit rule is load-bearing: assets in AND the sky settled (first
// world state) AND the town's materials compiled before the fade, each
// with a deadline so a dead sim or a stuck compile cannot hold the veil
// forever. The bar deliberately never reaches
// the end on its own; the fade finishes it.

import { useEffect, useState } from 'react'
import { useProgress } from '@react-three/drei'
import { Noto_Sans_JP } from 'next/font/google'
import useGameStore from '../../stores/gameStore'
import { LOADING_TIPS } from '../../lib/ui/loadingTips'

// 900 only: the whole point is the weight. preload false keeps it out of
// every other page's critical path; the katakana slice is tiny and the
// fallback stack paints instantly at bold while it arrives.
const notoJP = Noto_Sans_JP({ weight: '900', preload: false, display: 'swap' })

const FADE_MS = 900

// A floor on how long the veil is shown at all. Without it a warm cache
// flashes the veil for 80ms, which is worse than never showing it.
const MIN_SHOW_MS = 700

// How long to keep waiting for the world after the models are in. The sky
// settles when the first world state arrives (sky2 snaps and flags it);
// if the sim is down that flag never flips, and a veil that cannot end is
// worse than a sky that dims late.
//
// Was eight seconds, and eight seconds is what every visitor gets for a
// minute after each Render deploy, and what the emulated phone got for a
// minute on 23 Sep: the veil left on time and behind it was a black
// canvas with the chrome floating on it and nothing saying why. A minute
// now, with a line under the bar that says what it is waiting for, and a
// status pill after that if it still has not come.
const SKY_WAIT_MS = 60000
// When the wait starts explaining itself, and when it admits it is slow.
const EXPLAIN_MS = 5000
const SLOW_MS = 20000

// How long to wait for the town's materials to finish compiling once
// everything else is ready (Precompiler's boot pass: ~0.7 s on an M4 Pro,
// a few seconds on a slow phone). A compile that never reports must not
// hold the veil; leaving late costs a few hitches, not the page.
const COMPILE_WAIT_MS = 10000

// Long enough to read a two-line tip twice; slow enough that the veil
// never feels like a slideshow.
const TIP_MS = 6500

export function LoadingVeil() {
  const { progress, active, errors } = useProgress()

  // Name anything that failed to arrive: one 404'd model or texture parks
  // the progress counter short of 100 forever, and until now that failure
  // was invisible — the veil just never left.
  useEffect(() => {
    if (errors?.length) console.warn('[veil] assets that failed to load:', errors)
  }, [errors?.length])

  // Release valves. The manager's 100 is a promise it cannot always keep
  // (a single failed asset and it never arrives), and the world behind the
  // veil is usually fine — pulling the overlay off in devtools walked
  // straight into a loaded town. So: progress parked near the top for a
  // while counts as done, and past an absolute ceiling the veil leaves no
  // matter what. A rough entrance beats a curtain that never opens.
  const [stalledDone, setStalledDone] = useState(false)
  useEffect(() => {
    if (progress < 80) return
    const t = setTimeout(() => setStalledDone(true), 7000)
    return () => clearTimeout(t)   // any movement resets the stall clock
  }, [progress])
  useEffect(() => {
    const t = setTimeout(() => setStalledDone(true), 30000)
    return () => clearTimeout(t)
  }, [])
  const skySettled = useGameStore((s) => s.skySettled)
  const worldCompiled = useGameStore((s) => s.worldCompiled)
  const connected = useGameStore((s) => s.connectionStatus === 'connected')
  const [phase, setPhase] = useState('showing') // showing -> fading -> gone
  const [tip, setTip] = useState(0)
  useEffect(() => {
    if (LOADING_TIPS.length < 2) return
    // Dies with the veil. This ticked every 6.5s for the whole session
    // on a component rendering null, purely to rotate tips nobody could
    // see any more.
    if (phase === 'gone') return undefined
    const id = setInterval(() => setTip((n) => n + 1), TIP_MS)
    return () => clearInterval(id)
  }, [phase])

  const [mountedAt] = useState(() => Date.now())
  // When everything but the compile was ready, for COMPILE_WAIT_MS.
  const [readyAt, setReadyAt] = useState(null)
  // Bumped on a timer while waiting out the sky deadline, so the effect
  // below re-evaluates even when nothing else changes.
  const [, setTick] = useState(0)
  // What the veil is waiting for, said plainly once it has been a moment.
  // The town lives on another machine; a visitor should know that the
  // wait is that machine and not their phone. Set from the waiting loop
  // below, never computed in render.
  const [waitingLine, setWaitingLine] = useState(null)

  useEffect(() => {
    if (phase !== 'showing') return
    // `active` goes false when the loading manager has nothing in flight.
    // Guard on progress too: it starts at 0 with nothing queued yet, and
    // fading out before the first model is even requested would defeat the
    // point. stalledDone is the way out when 100 is never coming.
    const assetsDone = (!active && progress >= 100) || stalledDone
    if (!assetsDone) return

    const waited = Date.now() - mountedAt
    if (!skySettled && waited < SKY_WAIT_MS) {
      // Give the first world state a chance to land, then go anyway.
      const line = waited > SLOW_MS
        ? 'his town is slow to answer today, still trying'
        : waited > EXPLAIN_MS
          ? (connected ? 'his town is answering, one moment' : 'finding his town, it lives on another machine')
          : null
      const t = setTimeout(() => {
        setWaitingLine((prev) => (prev === line ? prev : line))
        setTick((n) => n + 1)
      }, 250)
      return () => clearTimeout(t)
    }

    // The town's materials compile behind the veil (Precompiler), so the
    // first turn is not a stutter. Waits at most COMPILE_WAIT_MS.
    if (!worldCompiled) {
      if (readyAt === null) {
        const t = setTimeout(() => setReadyAt(Date.now()), 0)
        return () => clearTimeout(t)
      }
      if (Date.now() - readyAt < COMPILE_WAIT_MS) {
        const t = setTimeout(() => setTick((n) => n + 1), 100)
        return () => clearTimeout(t)
      }
    }

    const hold = Math.max(0, MIN_SHOW_MS - waited)
    const t = setTimeout(() => setPhase('fading'), hold)
    return () => clearTimeout(t)
  })

  useEffect(() => {
    if (phase !== 'fading') return
    const t = setTimeout(() => setPhase('gone'), FADE_MS)
    return () => clearTimeout(t)
  }, [phase])

  if (phase === 'gone') return null

  return (
    <div
      aria-hidden={phase === 'fading'}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        // Warm near-black, the same family as the HUD panels, so the veil
        // and the chrome feel like one object.
        background: 'radial-gradient(120% 90% at 50% 45%, #241d1a 0%, #14100e 60%, #0d0a09 100%)',
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
      }}
    >
      {/* His name, said properly. The slight grow on exit makes the fade
          read as the town arriving rather than the veil giving up. */}
      <div
        className={notoJP.className}
        style={{
          color: 'rgba(255, 249, 242, 0.96)',
          fontSize: 'clamp(64px, 13vw, 118px)',
          fontWeight: 900,
          letterSpacing: '0.12em',
          marginLeft: '0.12em',
          lineHeight: 1,
          fontFamily: `${notoJP.style.fontFamily}, 'Hiragino Sans', 'Yu Gothic', sans-serif`,
          transform: phase === 'fading' ? 'scale(1.045)' : 'scale(1)',
          transition: `transform ${FADE_MS}ms ease`,
          userSelect: 'none',
        }}
      >
        ピノ
      </div>

      {/* The one moving thing. A hairline that fills left to right. */}
      <div
        className="mt-9 overflow-hidden"
        style={{
          width: 'min(240px, 52vw)',
          height: '2px',
          borderRadius: 2,
          background: 'rgba(255, 238, 222, 0.14)',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 2,
            // Never quite reaches the end on its own; the fade finishes it,
            // so it cannot sit at 100% looking stuck if a model is slow.
            width: `${Math.min(96, progress)}%`,
            background: 'rgba(255, 238, 222, 0.65)',
            transition: 'width 400ms ease',
          }}
        />
      </div>

      {/* One thing you can do here, per glance. Fixed-height slot so the
          veil never shifts as tips change; plain type, not italics: these
          are invitations, not quotations. */}
      <div style={{ marginTop: 34, minHeight: 72, maxWidth: 'min(420px, 84vw)', textAlign: 'center' }}>
        <style>{`@keyframes veil-tip { from { opacity: 0 } 8% { opacity: 1 } 88% { opacity: 1 } to { opacity: 0 } }`}</style>
        <p
          key={tip}
          style={{
            color: 'rgba(255, 238, 222, 0.62)',
            fontSize: 13.5,
            fontWeight: 600,
            lineHeight: 1.65,
            animation: `veil-tip ${TIP_MS}ms linear`,
          }}
        >
          {LOADING_TIPS[tip % LOADING_TIPS.length]}
        </p>
      </div>

      {/* Fixed slot so the tips above never move when this appears. */}
      <p
        style={{
          minHeight: 20,
          marginTop: 6,
          color: 'rgba(255, 238, 222, 0.45)',
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: '0.02em',
          textAlign: 'center',
        }}
      >
        {waitingLine || ''}
      </p>
    </div>
  )
}
